import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageLayout from "components/layout/PageLayout";
import { ORDER_STATUSES, ROUTES, STATUS_CODES } from "constants/index";
import { ORDERS_URL } from "constants/urls";
import { getOrderStatusChipColor, getOrderStatusLabel } from "helpers/orders";
import { MICROCONTROL_CLIENT_ID } from "helpers/operatorOrders";
import { queryBuilder } from "helpers/query";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { Order, OrderStatus } from "types";
import { typographyStyles } from "constants/styles";

const visibleStatuses: OrderStatus[] = [
  ORDER_STATUSES.INGRESADO,
  ORDER_STATUSES.EN_PREPARACION,
  ORDER_STATUSES.PREPARADO,
];

export default function OperatorOrdersPage() {
  const { data, fetchData, loading } = useCrud<Order>(ORDERS_URL, {
    disableAutoFetch: true,
  });
  const fetchCurrent = useRef(fetchData);
  fetchCurrent.current = fetchData;
  const initialized = useRef(false);
  const { showSnackBar } = useToast();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OrderStatus | "">(
    ORDER_STATUSES.INGRESADO,
  );
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(
    async (selectedStatus: OrderStatus | "") => {
      const response = await fetchCurrent.current(
        queryBuilder({
          clientId: MICROCONTROL_CLIENT_ID,
          status: selectedStatus || visibleStatuses,
        }),
        true,
        {},
        true,
      );
      setLoaded(true);
      setError(response.status !== STATUS_CODES.OK);
      if (response.status !== STATUS_CODES.OK)
        showSnackBar("No se pudieron cargar los pedidos.", "error");
    },
    [showSnackBar],
  );

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    void load(ORDER_STATUSES.INGRESADO);
  }, [load]);

  if (loading || !loaded) {
    return (
      <PageLayout
        pageTitle="Preparación de pedidos"
        pageDesc="Pedidos de Microcontrol"
      >
        <Typography role="status" textAlign="center">
          Cargando pedidos…
        </Typography>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        pageTitle="Preparación de pedidos"
        pageDesc="Pedidos de Microcontrol"
      >
        <Stack spacing={2} sx={{ maxWidth: 760, mx: "auto" }}>
          <Typography>No se pudieron cargar los pedidos.</Typography>
          <Button
            onClick={() => void load(status)}
            sx={{ alignSelf: "flex-start" }}
          >
            Reintentar
          </Button>
        </Stack>
      </PageLayout>
    );
  }

  const visible = data.filter(
    (order) =>
      order.client_id === MICROCONTROL_CLIENT_ID &&
      visibleStatuses.includes(order.movement_order?.order_status) &&
      (!status || order.movement_order.order_status === status),
  );

  return (
    <PageLayout
      pageTitle="Preparación de pedidos"
      pageDesc="Pedidos a realizar por el operador"
    >
      <Stack spacing={2} sx={{ maxWidth: 760, mx: "auto" }}>
        <Box sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
          <TextField
            label="Cliente"
            value="Microcontrol"
            disabled
            fullWidth
            size="small"
          />
          <TextField
            select
            label="Estado"
            value={status}
            onChange={(event) => {
              const selectedStatus = event.target.value as OrderStatus | "";
              setStatus(selectedStatus);
              void load(selectedStatus);
            }}
            disabled={loading}
            fullWidth
            size="small"
          >
            <MenuItem value="">Todos</MenuItem>
            {visibleStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {getOrderStatusLabel(status)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {visible.length === 0 && (
          <Typography textAlign="center" py={4}>
            No hay pedidos en este estado.
          </Typography>
        )}
        {visible.map((order) => (
          <Card key={order.id} variant="outlined">
            <CardActionArea
              onClick={() =>
                navigate(ROUTES.ORDERS_DETAIL.replace(":id", String(order.id)))
              }
              sx={{ p: 2 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Typography fontWeight={700}>Pedido #{order.id}</Typography>
                <Chip
                  size="small"
                  color={getOrderStatusChipColor(
                    order.movement_order.order_status,
                  )}
                  label={getOrderStatusLabel(order.movement_order.order_status)}
                />
              </Stack>
              <Typography
                sx={{ ...typographyStyles.monospace, fontWeight: 500 }}
              >
                Remito: {order.client_document || "Sin documento"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Unidades totales: {order.movement_order.total_units}
              </Typography>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </PageLayout>
  );
}
